import * as React from 'react'

import { cn } from '@/utils'

type CheckedState = boolean | 'indeterminate'

interface CheckboxProps
  extends Omit<
    React.ComponentPropsWithoutRef<'input'>,
    'checked' | 'onChange'
  > {
  checked?: CheckedState
  onCheckedChange?: (checked: boolean) => void
}

const Checkbox = React.memo(
  React.forwardRef<HTMLInputElement, CheckboxProps>(
    ({ className, checked, onCheckedChange, ...props }, ref) => {
      const inputRef = React.useRef<HTMLInputElement | null>(null)
      const isIndeterminate = checked === 'indeterminate'
      const isChecked = checked === true

      React.useLayoutEffect(() => {
        if (inputRef.current) {
          inputRef.current.indeterminate = isIndeterminate
        }
      }, [isIndeterminate])

      const handleChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
          e.stopPropagation()
          onCheckedChange?.(e.target.checked)
        },
        [onCheckedChange],
      )

      return (
        <input
          ref={(el) => {
            inputRef.current = el
            if (typeof ref === 'function') {
              ref(el)
            } else if (ref) {
              ref.current = el
            }
          }}
          type="checkbox"
          checked={isChecked}
          onChange={handleChange}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'h-4 w-4 shrink-0 rounded-sm border-primary accent-primary cursor-pointer',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
          {...props}
        />
      )
    },
  ),
)
Checkbox.displayName = 'Checkbox'

export { Checkbox }
export type { CheckboxProps, CheckedState }
